import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBarModule, MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { PriceList } from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Subject, fromEvent } from 'rxjs';
import { concatMap, debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';

import { NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FuseDirectivesModule } from '@fuse/directives/directives';
import { OrderDetailService } from '../../order-detail.service';
import { PriceListProductGroupsComponent } from './price-list-product-groups/price-list-product-groups.component';
import { PriceListProductsCardedComponent } from './price-list-products-carded/price-list-products-carded.component';
import { PriceListProductsComponent } from './price-list-products/price-list-products.component';
import { PriceListProductDetailComponent } from './product-detail/product-detail.component';

@Component({
  templateUrl: './add-product-dialog.component.html',
  styleUrls: ['./add-product-dialog.component.scss'],
  standalone: true,
  imports: [
    NgSwitch,
    NgSwitchCase,
    NgClass,
    NgIf,
    NgFor,
    MatIconModule,
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule,
    MatDividerModule,
    PriceListProductsComponent,
    PriceListProductsCardedComponent,
    PriceListProductGroupsComponent,
    PriceListProductDetailComponent,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    FuseDirectivesModule,
    MatLegacySnackBarModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AddElasticModelDialogComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() productNum = 1;
  @Input() priceLists: PriceList[] = [];

  @ViewChildren('productsList', { emitDistinctChangesOnly: false }) productsList!: QueryList<
    PriceListProductsCardedComponent | PriceListProductsComponent
  >;
  @ViewChild('productDetail') productDetail: PriceListProductDetailComponent;
  @ViewChild('productSearch') productSearch: ElementRef;
  @ViewChild('productsWrapper') productsWrapper: ElementRef;

  productsViewMode: 'view_list' | 'view_module' = 'view_module';

  productFormValid = false;

  unsubscribe$: Subject<void> = new Subject();

  selectedGroupId = LocalStorageManager.selectedProductGroupId;
  oldSelectedGroupId = 0;
  selectedProductId = 0;

  selectedPriceList: PriceList;

  loading = false;

  constructor(
    public dialogRef: MatDialogRef<AddElasticModelDialogComponent>,
    private orderDetailService: OrderDetailService,
    private snackBar: MatSnackBar
  ) {}

  userId(): number {
    return LocalStorageManager.regData;
  }

  ngOnInit(): void {
    if (this.priceLists && LocalStorageManager.selectedPriceListId === 0) {
      this.selectedPriceList = this.priceLists[0];
      return;
    }

    this.selectedPriceList = this.priceLists.find(p => p.id === LocalStorageManager.selectedPriceListId);
  }

  ngAfterViewInit(): void {
    if (LocalStorageManager.selectedProductGroupId > 0) this.updateProducts(this.selectedGroupId);

    this.productsList.changes.pipe(debounceTime(50), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updateProducts(this.selectedGroupId);
    });

    fromEvent(this.productSearch.nativeElement, 'keyup')
      .pipe(debounceTime(700), distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe((v: KeyboardEvent) => {
        if (this.productsList && this.productsList.length > 0) {
          this.productsList.first.filter.next((v.target as HTMLInputElement).value);
        }
      });
  }

  updateProducts(groupId: number): void {
    if (this.productsList && this.productsList.length > 0) {
      this.productsList.first.selectedGroupIdChange.next(groupId);

      if (this.productSearch.nativeElement.value) {
        this.productsList.first.filter.next(this.productSearch.nativeElement.value);
      }
    }
  }

  showProductCard(productId: number): void {
    this.selectedProductId = productId;
  }

  toggleViewMode(): void {
    this.productsViewMode = this.productsViewMode === 'view_module' ? 'view_list' : 'view_module';
  }

  groupChanged(groupId: number): void {
    this.selectedGroupId = groupId;

    if (this.oldSelectedGroupId != 0) {
      this.selectedGroupId = this.oldSelectedGroupId;
      this.productsList.first.selectedGroupIdChange.next(this.selectedGroupId);
      this.oldSelectedGroupId = 0;
    }

    LocalStorageManager.selectedProductGroupId = this.selectedGroupId;
    this.productsWrapper.nativeElement.scrollTop = 0;
  }

  saveAndClose(): void {
    const product = this.productDetail.productChange.value;

    for (const element of product.elements) {
      if (element.edgeList && element.edgeList.some(e => _.isEmpty(e.name) || _.isNil(e.name))) {
        this.snackBar.open($localize`Необходимо назначить облицовку для элементов`);
        return;
      }
    }

    this.loading = true;

    this.orderDetailService
      .addCuttingProduct(this.selectedProductId, product)
      .pipe(
        concatMap(p => this.orderDetailService.cuttingProductChangeMaterials(p)),
        takeUntil(this.unsubscribe$),
        finalize(() => (this.loading = false))
      )
      .subscribe(
        () => {
          this.dialogRef.close(true);
        },
        () => {
          this.snackBar.open($localize`Не удалось добавить изделие`);
        }
      );
  }

  priceListChanged(priceList: PriceList): void {
    LocalStorageManager.selectedPriceListId = priceList.id;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
